import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";

import styles from "./demo.module.scss";
import { fetchDemoPanel, panelKey, rememberPanelKey } from "./demoService";
import { BRAND, PRODUCT_URL, campaignUrl, sourceLabel } from "./offer";

const cx = (...names) => names.filter(Boolean).map((name) => styles[name] || name).join(" ");

function Locked({ slug }) {
  return (
    <div className={cx("root")} lang="en">
      <main className={cx("panel", "gate")}>
        <img className={cx("lockup-full")} src={`${BRAND}/lockup-header.png`} alt="Sonic Prospect by Pivony" />
        <p>Your panel opens from the private link in our email.</p>
        <a className={cx("btn", "p")} href={campaignUrl(PRODUCT_URL, slug, "panel")}>
          Contact Pivony
        </a>
      </main>
    </div>
  );
}

const ProspectDemoPanel = () => {
  const { slug } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    // The key arrives once in the emailed link; keep it for this tab and drop it from the address bar.
    const fromLink = new URLSearchParams(location.search).get("key") || "";
    if (fromLink) {
      rememberPanelKey(slug, fromLink);
      history.replace(`/demos/${encodeURIComponent(slug)}/panel`);
    }
    const key = fromLink || panelKey(slug);
    if (!key) {
      setState({ loading: false, status: 403 });
      return undefined;
    }
    let alive = true;
    fetchDemoPanel(slug, key).then(({ status, body }) => {
      if (alive) setState({ loading: false, status, panel: body });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const panel = state.panel || {};
  useEffect(() => {
    document.title = panel.name ? `${panel.name} panel` : "Demo panel";
  }, [panel.name]);

  if (state.loading) return <div className={cx("root")} lang="en" />;
  if (state.status !== 200) return <Locked slug={slug} />;

  const counts = panel.counts || {};
  return (
    <div className={cx("root")} lang="en">
      <main className={cx("panel")}>
        <header className={cx("panel-nav")}>
          <img className={cx("icon")} src={`${BRAND}/icon-white@2x.png`} alt="" />
          <img className={cx("word")} src={`${BRAND}/wordmark-white@2x.png`} alt="Sonic Prospect" />
        </header>
        <p className={cx("kicker")}>Prepared for {panel.name} by Pivony</p>
        <h1>{panel.name} panel</h1>
        <p className={cx("fine")}>
          {panel.state === "claimed" ? "Now part of a Pivony account" : "Demo"} · visits {counts.visits || 0} · chats{" "}
          {counts.chats || 0} · pricing opens {counts.pricing_opens || 0} · starts {counts.starts || 0}
        </p>
        <p>
          <a className={cx("btn", "p")} href={`/demos/${encodeURIComponent(slug)}`}>
            Open the demo
          </a>
        </p>
        <section>
          <h2>What your assistant read</h2>
          <p>{(panel.pages || []).length} pages indexed.</p>
          <ul>
            {(panel.pages || []).map((url) => (
              <li key={url}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </a>
              </li>
            ))}
          </ul>
          <p>Topics: {(panel.topics || []).join(", ")}</p>
        </section>
        <section>
          <h2>Conversations</h2>
          {(panel.conversations || []).length === 0 && <p>No conversations yet.</p>}
          {(panel.conversations || []).map((row, index) => (
            <article key={index}>
              <p>
                <strong>{row.channel}</strong> · {row.question}
              </p>
              <p>{row.answer}</p>
              {row.source_url && <p className={cx("fine")}>Learned from: {sourceLabel(row.source_url)}</p>}
            </article>
          ))}
        </section>
      </main>
    </div>
  );
};

export default ProspectDemoPanel;
